import { type FC, useCallback, useMemo, useState } from 'react';

import { Alert } from '@vakantiesnl/components/src/atoms/Alert';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Link } from '@vakantiesnl/components/src/atoms/Link/Link';
import { Tag } from '@vakantiesnl/components/src/atoms/Tag';
import { StepTitle } from '@vakantiesnl/components/src/blocks/accommodation/StepTitle/StepTitle';
import { getExtraPriceTitlePerPerson } from '@vakantiesnl/components/src/molecules/FlightSelector/helpers';
import { ModalOrDrawer } from '@vakantiesnl/components/src/molecules/ModalOrDrawer/ModalOrDrawer';
import { RichText } from '@vakantiesnl/components/src/molecules/RichText';
import { RoomInfoModal } from '@vakantiesnl/components/src/molecules/RoomInfoModal';
import { SingleSelect } from '@vakantiesnl/components/src/molecules/SelectBar';
import { getRoomDescription } from '@vakantiesnl/components/src/utils/accommodation';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useAccommodationContext } from '@vakantiesnl/services/src/context/useAccommodationContext';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { useRichText } from '@vakantiesnl/services/src/hooks/contentful';
import { useGetAccommodationByEntityId } from '@vakantiesnl/services/src/hooks/queries';
import { usePartyComposition } from '@vakantiesnl/services/src/stores';
import { buildSummarizedPartyString } from '@vakantiesnl/services/src/util/partyUtils';
import { type Search } from '@vakantiesnl/types';
import { isEmpty } from 'lodash';
import dynamic from 'next/dynamic';

const CompassLoader = dynamic(
	() =>
		import('@vakantiesnl/components/src/__LEGACY__/atoms/CompassLoader').then((mod) => ({
			default: mod.CompassLoader,
		})),
	{ ssr: false },
);

import { useCustomStyles } from './RoomType.styles';

const rteFilter = {
	['fields.key[in]']: ['roomtype.info'],
};

type RoomAttributeState = { attributes?: string[]; roomTitle?: string; images: { url: string }[] | null };

const RoomType: FC = () => {
	const { classes } = useCustomStyles();
	const microCopies = useMicroCopyContext();
	const { accommodation } = useGetAccommodationByEntityId();
	const {
		state: { roomData, flightData },
		dispatch: { setRoomOffer },
	} = useAccommodationContext();
	const {
		dispatch: { updateSelectedRoomType, updateSelectedMealPlan },
	} = useTripFilterContext();
	const party = usePartyComposition();

	const [modalAttributesContent, setModalAttributesContent] = useState<RoomAttributeState | undefined>();
	const [showRoomInfoModal, setShowRoomInfoModal] = useState<boolean>(false);

	const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
	const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>([]);

	const mappedRichTextElements = useRichText(rteFilter);
	const document = mappedRichTextElements && mappedRichTextElements['roomtype.info'];

	// This object holds the possible offers for each room type at each room position
	const roomsOffers = useMemo(() => {
		if (!roomData || roomData.loading) return [];

		const result: { [roomCode: string]: Search.OfferItem[] }[] = [];

		// For each room index the possible room types will be calculated from the offers
		// with storing the offer object for that index/type combination
		for (const offer of roomData.roomOffers) {
			for (let index = 0; index < offer.rooms.length; index++) {
				const room = offer.rooms[index];
				result[index] ??= {};
				result[index][room.opCode] ??= [];
				result[index][room.opCode].push(offer);
			}
		}

		// For each room index we sort the offers from the cheapest to the most expensive
		// for each room type to be able to select the cheapest offer when two have the
		// room type for that index
		for (const room of result) {
			for (const roomKey of Object.keys(room)) {
				room[roomKey].sort((a, b) => a.pricePerAdult - b.pricePerAdult);
			}
		}

		return result;
	}, [roomData]);

	const closeRoomInfoModal = useCallback(() => setShowRoomInfoModal(false), []);

	const toggleRoomInfoModal = useCallback(
		(offer: Search.OfferItem) => () => {
			if (offer.productInfo) {
				setShowRoomInfoModal(!showRoomInfoModal);
				setModalAttributesContent({
					images: offer.productInfo.images
						? offer.productInfo.images.filter(({ is_thumb }) => !is_thumb)
						: null,
					attributes: offer.productInfo.properties
						.filter((property) => property.descriptions.length > 0)
						.map((property) => {
							if (property.quantity > 0)
								return `${property.descriptions?.[0].description}: ${property.quantity}`;
							return property.descriptions?.[0].description;
						}),
					roomTitle: offer.productInfo.name,
				});
			}
			if (!showRoomInfoModal) {
				import('@vakantiesnl/services/src/gtm/common').then(({ track }) => {
					import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ onRoomTypeOpen }) =>
						track(onRoomTypeOpen(accommodation)),
					);
				});
			}
		},
		[showRoomInfoModal, accommodation],
	);

	const renderRoomParagraph = useCallback(
		(offer: Search.OfferItem) => {
			const hasProductInfoImages = !!offer.productInfo?.images.length;
			const hasProductInfoProperties = !!offer.productInfo?.properties.filter(
				(property) => property.descriptions.length > 0,
			).length;

			if (hasProductInfoImages || hasProductInfoProperties) {
				return (
					<>
						<Link onClick={toggleRoomInfoModal(offer)} data-cy="room-type-more-info-button">
							{microCopies['moreInfo']}
						</Link>
					</>
				);
			}
			return null;
		},
		[microCopies, toggleRoomInfoModal],
	);

	const toggleInfoModal = useCallback(() => {
		setShowInfoModal(!showInfoModal);
		if (!showInfoModal) {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) => {
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ onRoomTypeOpen }) =>
					track(onRoomTypeOpen(accommodation)),
				);
			});
		}
	}, [showInfoModal, accommodation]);

	const onRoomTypeSelect = useCallback(
		(offer: Search.OfferItem, label: string, priceDifference: number | undefined) => (): void => {
			setRoomOffer(offer);
			setSelectedRoomTypes(offer.rooms.map((room) => room.opCode));
			updateSelectedRoomType(offer?.rooms.map((room) => room.opCode).join('_'));
			updateSelectedMealPlan(undefined);

			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ roomSelect }) => {
					track(roomSelect(accommodation, offer, label, priceDifference));
				}),
			);
		},
		[setRoomOffer, updateSelectedRoomType, accommodation, updateSelectedMealPlan],
	);

	// This helper checks if the offer is selectable or not by comparing the prefix of the offer
	// room types and the prefix of the selected offer room types up to the room index
	const isOfferSelectable = useCallback(
		(offer: Search.OfferItem, roomIndex: number) => {
			for (let index = 0; index < roomIndex; index++) {
				if (!isEmpty(selectedRoomTypes[index]) && offer.rooms[index].opCode !== selectedRoomTypes[index]) {
					return false;
				}
			}

			return true;
		},
		[selectedRoomTypes],
	);

	if (roomData?.loading || flightData?.loading) {
		return (
			<div data-cy="room-type-selector">
				<StepTitle className={classes.titleWrapper} title={microCopies['accommodation.room_type.title']} />
				<CompassLoader />
			</div>
		);
	}

	if (roomData === null || !roomData.roomOffers.length) {
		return (
			<div data-cy="room-type-selector">
				<StepTitle className={classes.titleWrapper} title={microCopies['accommodation.room_type.title']} />
				<Alert variant="warning" text={microCopies['roomfilters.noresults']} />
			</div>
		);
	}

	const previousSelectedOfferPrice = roomData.previousSelectedOffer?.pricePerAdult || 0;

	return (
		<div data-cy="room-type-selector">
			<div className={classes.titleWrapper}>
				<StepTitle title={microCopies['accommodation.room_type.title']} />
				<Button size="small" leadingIcon="info" onClick={toggleInfoModal} variant="tertiary" />
			</div>
			<div className={classes.roomsContainer}>
				{roomsOffers.map((groupedOffers, roomIndex) => (
					<div key={roomIndex} className={classes.roomGroup}>
						{party.length > 1 && (
							<span className={classes.roomTitle}>
								{microCopies['common.room']} {roomIndex + 1}{' '}
								<span className={classes.roomPersonsCount}>
									(
									{buildSummarizedPartyString(
										party[roomIndex] ? [party[roomIndex]] : [],
										microCopies,
									)}
									)
								</span>
							</span>
						)}
						{Object.values(groupedOffers)
							.map((offers) => {
								return (
									offers.find((offer) => offer.id === roomData.selected) ??
									offers.find((offer) => isOfferSelectable(offer, roomIndex))
								);
							})
							.sort((offer1, offer2) => {
								if (!offer1 || !offer2) return 0;
								return offer1.pricePerAdult - offer2.pricePerAdult;
							})
							.map((offer, key) => {
								if (!offer) return;

								const priceDifference =
									previousSelectedOfferPrice - offer.pricePerAdult === 0
										? undefined
										: offer.pricePerAdult - previousSelectedOfferPrice;
								const optionTitle = getRoomDescription(offer, roomIndex, microCopies);

								return (
									<SingleSelect
										key={`roomtype-${key}`}
										title={optionTitle}
										selected={offer.id === roomData.selected}
										id={`roomtype-${offer.id}`}
										value={offer.id}
										name={`roomtype-${roomIndex}`}
										dataCy="roomTypeOption"
										tag={
											<Tag
												color={'orange'}
												variant={priceDifference ? 'outline' : 'filled'}
												size="small"
											>
												{priceDifference
													? getExtraPriceTitlePerPerson(priceDifference)
													: microCopies['cheapestOption']}
											</Tag>
										}
										onSelect={onRoomTypeSelect(offer, optionTitle, priceDifference)}
									>
										{roomIndex === 0 && renderRoomParagraph(offer)}
									</SingleSelect>
								);
							})}
					</div>
				))}
			</div>
			{showInfoModal && document && (
				<ModalOrDrawer
					title={microCopies['roomtypes.info.title']}
					open={showInfoModal}
					onClose={toggleInfoModal}
					hasCloseIcon
				>
					<RichText document={document} />
				</ModalOrDrawer>
			)}
			{modalAttributesContent?.attributes && modalAttributesContent?.roomTitle && (
				<RoomInfoModal
					title={modalAttributesContent.roomTitle}
					attributes={modalAttributesContent.attributes}
					onClose={closeRoomInfoModal}
					images={modalAttributesContent.images}
					open={showRoomInfoModal}
					loader={false}
				/>
			)}
		</div>
	);
};

export default RoomType;
